/**
 * Utility functions for parsing audio point IDs consistently across the application
 */

export interface AudioPointIdParseResult {
  /** Whether this is a temporary ID (like 'audio-50-75') */
  isTemporary: boolean;
  /** The extracted numeric ID for backend operations, null if temporary */
  numericId: number | null;
  /** Whether the ID format is valid */
  isValid: boolean;
  /** The original ID string */
  originalId: string;
}

/**
 * Parses audio point IDs safely and consistently.
 * Handles temporary template IDs (like 'audio-50-75') and real backend IDs.
 *
 * @param id - The audio point ID to parse
 * @returns Parsed ID information
 */
export function parseAudioPointId(
  id: string | number
): AudioPointIdParseResult {
  const idString = String(id);

  // Handle temporary template IDs (like 'audio-50-75')
  if (idString.startsWith('audio-')) {
    return {
      isTemporary: true,
      numericId: null,
      isValid: true,
      originalId: idString
    };
  }

  // Handle real backend IDs
  // For composite formats like 'prefix-123', extract the numeric part
  let numericPart: string;

  if (idString.includes('-')) {
    // For composite IDs like 'prefix-123', take the LAST part after splitting by '-'
    // This is more robust than taking the first part
    const parts = idString.split('-');
    numericPart = parts[parts.length - 1];
  } else {
    // For simple numeric or mixed strings, extract all digits
    numericPart = idString.replace(/\D/g, '');
  }

  const numericId = parseInt(numericPart, 10);

  // Validate the extracted numeric ID
  const isValid = !isNaN(numericId) && numericId >= 0 && numericPart.length > 0;

  return {
    isTemporary: false,
    numericId: isValid ? numericId : null,
    isValid,
    originalId: idString
  };
}

/**
 * Checks if an audio point ID represents a temporary audio point.
 * @param id - The audio point ID to check
 * @returns True if the ID is temporary (not saved to backend yet)
 */
export function isTemporaryAudioPointId(id: string | number): boolean {
  return parseAudioPointId(id).isTemporary;
}

/**
 * Extracts the numeric backend ID from an audio point ID.
 * @param id - The audio point ID to extract from
 * @returns The numeric ID for backend operations, or null if not applicable
 */
export function extractNumericId(id: string | number): number | null {
  return parseAudioPointId(id).numericId;
}
